import React, { useState } from 'react';
import { Download, FileText, AlertCircle, TrendingUp, Users, DollarSign, Heart, ChevronDown, ChevronUp } from 'lucide-react';

const ReportGeneration = ({ response, agentName }:any) => {
  const [expandedSections, setExpandedSections] = useState({});
  const reports = response?.response?.report || response?.report || {};



  const getImpactColor = (impact:any) => {
    switch(impact) {
      case 'Severe': return 'bg-red-100 text-red-800 border-red-300';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Helper function to safely render array content
  const renderArrayContent = (items:any) => {
    if (!items || !Array.isArray(items)) return null;
    return items.map((item, index) => (
      <p key={index} className="text-xs text-slate-700 leading-relaxed mb-1">
        • {typeof item === 'string' ? item : JSON.stringify(item)}
      </p>
    ));
  };

  return (
    <div className="h-[90vh] max-w-2xl border rounded-xl border-gray-300">
      {/* Header */}
      <div className="flex justify-between text-black p-3 border-b border-gray-200">
        <div>
          <h1 className="text-md font-bold mb-1">{agentName === "quantitative-agent" ? "Quantitative Agent" : "Mr Insights Agent" }</h1>
          <p className="text-xs opacity-95">Patient & Caregiver Insights Analysis</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-2 space-y-4 h-[63vh] overflow-y-auto pn_scroller">
        {/* Executive Summary */}
        {reports['Executive Summary'] && (
          <section className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-2 border-l-4 border-purple-600">
            <h2 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-2 h-2 text-purple-600" />
              Executive Summary
            </h2>
            <p className="text-gray-700 mb-4 leading-relaxed text-xs">{reports['Executive Summary'].overview}</p>
            <div className="bg-white rounded-lg p-2 mb-4 border border-purple-200">
              <h3 className="font-bold text-purple-700 mb-1 text-xs">Core Insight</h3>
              <p className="text-gray-700 text-xs">{reports['Executive Summary'].core_insight}</p>
            </div>
            <div className="grid gap-4">
              <div className="bg-white rounded-lg p-2 border border-red-200">
                <h3 className="font-bold text-red-700 mb-1 text-sm">Top Hesitation Drivers</h3>
                <ul className="space-y-1">
                  {reports['Executive Summary'].top_hesitation_drivers?.map((driver:any, idx:number) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <span className="text-red-500 font-bold text-xs">•</span>
                      <span className='text-xs'>{driver}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-lg p-2 border border-green-200">
                <h3 className="font-bold text-green-700 mb-1 text-sm">Confidence Factors</h3>
                <ul className="space-y-1">
                  {reports['Executive Summary'].confidence_factors?.map((factor:any, idx:number) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <span className="text-green-500 font-bold text-xs">•</span>
                      <span className='text-xs'>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Sentiment Landscape */}
        {reports['Sentiment & Emotion Landscape'] && (
          <section className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
            <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-sm">
              <Heart className="w-2 h-2 text-pink-600" />
              Sentiment & Emotion Landscape
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-3">
              <div className="bg-blue-50 rounded-lg p-1 border border-blue-200">
                <div className="text-sm text-blue-700 font-semibold mb-1">Overall Sentiment Score</div>
                <div className="text-sm font-bold text-blue-800">
                  {reports['Sentiment & Emotion Landscape'].overall_sentiment_score}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-1 border border-green-200">
                <div className="text-sm text-green-700 font-semibold mb-1">Hope Index</div>
                <div className="text-sm font-bold text-green-800">
                  {reports['Sentiment & Emotion Landscape'].hope_index}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {reports['Sentiment & Emotion Landscape'].emotions?.map((emotion:any, idx:number) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-2 border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-gray-800 text-sm">{emotion.emotion}</h3>
                    <span className="bg-purple-600 text-white p-1 rounded-full text-sm font-semibold">{emotion.frequency}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2"><strong>Trigger:</strong> {emotion.key_trigger}</p>
                  <blockquote className="border-l-4 border-purple-400 pl-4 italic text-gray-700 bg-white p-1 text-sm rounded-r">
                    "{emotion.example_quote}"
                  </blockquote>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Key Adoption Barriers */}
        {reports['Key Adoption Barriers'] && (
          <section className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-2 h-2 text-orange-600" />
              Key Adoption Barriers
            </h2>
            <div className="space-y-2">
              {reports['Key Adoption Barriers'].map((barrier:any, idx:number) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-2 border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="bg-purple-600 text-white w-3 h-3 rounded-full flex items-center justify-center font-bold text-sm">
                        {barrier.rank}
                      </span>
                      <h3 className="font-bold text-gray-800 text-xs">{barrier.barrier}</h3>
                    </div>
                    <span className={`p-1 rounded-full text-xs font-semibold border ${getImpactColor(barrier.impact)}`}>
                      {barrier.impact} Impact
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-xs">{barrier.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Persona Segmentation - FIXED */}
        {reports['Persona Segmentation'] && (
          <section className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-2 h-2 text-indigo-600" />
              Persona Segmentation
            </h2>
            <div>
              {Array.isArray(reports['Persona Segmentation']) ? (
                reports['Persona Segmentation'].map((persona, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-2 border border-indigo-200 hover:shadow-lg transition-shadow mb-2">
                    <div className="text-xs mb-1">{persona.persona}</div>
                    <div className="bg-white rounded px-3 py-1 inline-block mb-1 text-xs font-semibold text-indigo-700">
                      {persona.percentage}
                    </div>
                    <p className="text-xs text-gray-700 mb-2">{persona.mindset_summary}</p>
                    
                    {persona.support_priorities && (
                      <div>
                        <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">
                          Support Priorities
                        </p>
                        {persona.support_priorities.map((priority:any, pIdx:number) => (
                          <p key={pIdx} className="text-xs text-slate-700 leading-relaxed">
                            • {priority}
                          </p>
                        ))}
                      </div>
                    )}
                    
                    {persona.characteristics && (
                      <div className="mt-2">
                        <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">
                          Characteristics
                        </p>
                        {renderArrayContent(persona.characteristics)}
                      </div>
                    )}
                    
                    {persona.challenges && (
                      <div className="mt-2">
                        <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">
                          Challenges
                        </p>
                        {renderArrayContent(persona.challenges)}
                      </div>
                    )}
                    
                    {persona.motivations && (
                      <div className="mt-2">
                        <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">
                          Motivations
                        </p>
                        {renderArrayContent(persona.motivations)}
                      </div>
                    )}
                  </div>
                ))
              ) : null}
            </div>
          </section>
        )}

        {/* Support Strategy Recommendations */}
        {reports['Support Strategy Recommendations'] && (
          <section className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-2">
              <TrendingUp className="w-2 h-2 text-green-600" />
              Support Strategy Recommendations
            </h2>
            <div className="space-y-3">
              {reports['Support Strategy Recommendations'].map((strategy:any, idx:number) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="bg-green-600 text-white px-2 py-1 rounded-md font-bold text-xs">
                        {strategy.priority}
                      </span>
                      <h3 className="font-semibold text-gray-800 text-sm">
                        {strategy.initiative}
                      </h3>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      strategy.expected_impact === "High"
                        ? "bg-green-100 text-green-800 border border-green-300"
                        : "bg-yellow-100 text-yellow-800 border border-yellow-300"
                    }`}>
                      {strategy.expected_impact} Impact
                    </span>
                  </div>
                  <p className="text-gray-700 text-xs mb-3">{strategy.description}</p>
                  
                  {strategy.evidence_sources && strategy.evidence_sources.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 mt-2">
                      <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase">
                        External Sources
                      </h4>
                      <ul className="space-y-2">
                        {strategy.evidence_sources.map((src:any, sidx:number) => (
                          <li key={sidx} className="flex items-start gap-3 text-xs bg-gray-50 rounded-md p-2 border border-gray-100">
                            {src.favicon_url && (
                              <img src={src.favicon_url} alt="" className="w-4 h-4 rounded-sm mt-0.5" />
                            )}
                            <div className="flex-1">
                              <a href={src.url} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline block">
                                {src.title}
                              </a>
                              <p className="text-gray-600 text-[11px] mt-1">{src.summary}</p>
                              {src.reliability_score && (
                                <span className="inline-block mt-2 text-[10px] bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md border border-indigo-200">
                                  Reliability Score: {src.reliability_score}
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Quantitative Index Summary */}
        {reports['Quantitative Index Summary'] && (
          <section className="bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-2 h-2 text-blue-600" />
              Quantitative Index Summary
            </h2>
            <div>
              {Object.entries(reports['Quantitative Index Summary']).map(([key, data]:any, idx) => (
                <div key={idx} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2 border border-blue-200 mb-2">
                  <h3 className="font-bold text-gray-800 mb-1 text-xs">{key}</h3>
                  <div className="text-xs font-bold text-blue-600 mb-1">{data.value}</div>
                  <p className="text-xs text-gray-600 mb-1">{data.definition}</p>
                  <span className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {data.interpretation}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Next Steps */}
        {reports['Next Steps for PharmCART'] && (
          <section className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-2 border-l-4 border-purple-600">
            <h2 className="text-sm font-bold text-gray-800 mb-1">Next Steps for PharmCART</h2>
            <div className="space-y-2">
              {reports['Next Steps for PharmCART'].map((step:any, idx:number) => (
                <div key={idx} className="bg-white rounded-lg p-2 border border-gray-200 hover:shadow-md transition-shadow flex items-start gap-4">
                  <span className="bg-purple-600 text-white w-2 h-2 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {step.step}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 mb-1 text-xs">{step.action}</h3>
                    <div className="flex flex-wrap gap-1 text-xs text-gray-600">
                      <span><strong>Owner:</strong> {step.owner}</span>
                      <span><strong>Timeline:</strong> {step.timeline}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-100 p-3 text-center text-gray-600 text-sm">
        <p>© 2025 PharmCART | CAR-T Therapy Adoption Analysis Report</p>
      </div>
    </div>
  );
};

export default ReportGeneration;